import { Formik, FormikHelpers } from 'formik';
import { useAlert,
  CheckboxWithLabel,
  ExternalLink, OptionalBehindButton, PasswordField, TextInput,
  useUpdateMobileNumber, MaskedInput,
} from '@uplift/ui-v2';
import * as Yup from 'yup';

import { Box, FormGroup, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  authExchangeCode, authLogin, AuthStatus, getUserMatches, patchUser, patchUserProfile, setPassword
} from '@uplift/api';
import { PASSWORD_MATCHES } from '@uplift/constants';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n, useStore } from '@uplift/hooks';
import { l10nEnService } from '@uplift/services';

import {
  StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../components';
import { CreateAccountNextData } from '../types';
import { useBetterhelpReferralDetails } from '../useBetterhelpUserDetails.hook';
import { VALIDATION_MOBILE_PHONE, applyNameValidator, isValidEmail } from '@uplift/validations';
import { ContactName } from '@uplift/types';
import { getFormErrorsFromRewriteUserContactByNameResponse } from '@uplift/utils';

export type CreateAccountProps = {
  onBack: () => void;
  onNext: (data: CreateAccountNextData) => void;
  inviteToken: string;
};

export const CreateAccount: React.VFC<CreateAccountProps> = props => {
  const queryClient = useQueryClient();
  const { updateMobileNumber } = useUpdateMobileNumber();
  const { authStore } = useStore();
  const { classes } = useCreateAccountStyles();
  const l10n = useL10n();
  const alert = useAlert();
  const currentUser = useCurrentuser();
  const betterhelpData = useBetterhelpReferralDetails(props.inviteToken);

  const onSubmit = async (values: CreateAccountFormType, formikHelpers: FormikHelpers<CreateAccountFormType>) => {
    await setPassword({
      token: props.inviteToken,
      password: values.password,
      isInviteToken: true,
    });
    const loginResult = await authLogin({
      username: values.email,
      password: values.password,
    });

    if (!('error' in loginResult) && loginResult.status === AuthStatus.REDIRECT) {
      const tokens = await authExchangeCode(loginResult.code);

      authStore.setTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
      authStore.setTokensInLocalStorage();

      const userId = loginResult.id;

      // update contacts
      try {
        await updateMobileNumber({ name: ContactName.mobile, number: values.mobilePhone, userId: userId });

        await queryClient.invalidateQueries({ queryKey: [ 'user', currentUser.id, 'contacts' ] });
      } catch (error) {
        authStore.clear();
        const errorObj = getFormErrorsFromRewriteUserContactByNameResponse(error, ContactName.mobile);

        if (errorObj && errorObj[ContactName.mobile]) {
          formikHelpers.setErrors({ mobilePhone: errorObj[ContactName.mobile] });
          return;
        }

        console.error('Error updating contacts', error);
        return;
      }

      await patchUser(userId, {
        firstName: values.firstName,
        lastName: values.lastName,
      });

      if (values.preferredName) {
        await patchUserProfile(userId, { preferredName: values.preferredName });
      }

      await queryClient.invalidateQueries({ queryKey: [ 'betterhelp-invite', props.inviteToken ] });

      const matches = await getUserMatches({
        userId,
        states: [ 'initial', 'active' ],
        forceMatchCreateIfMissing: true
      });
      const initialMatch = matches.data.matches[0];

      props.onNext({ isLoggedIn: true, matchId: initialMatch?.id });
      return;
    }

    alert.error('An error occurred. Please try again.');
  };

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema(l10n)}
      initialValues={{
        firstName: betterhelpData?.firstName,
        lastName: betterhelpData?.lastName,
        email: betterhelpData?.email,
        mobilePhone: '',
        preferredName: '',
        password: '',
        privacyPolicy: false,
        treatmentConsent: false,
        noticeOfPrivacyPolicy: false,
      }}
      enableReinitialize
      validateOnMount
    >
      {formik => {

        if (authStore.isLoggedIn && !formik.isSubmitting) {
          return (
            <StepContainer>
              <StepContent className={classes.accountCreatedContent}>
                <CheckCircle className={classes.checkCircleIcon} data-testid="green-check-icon" />
                <StepHeading className={classes.stepHeader} data-testid="password-set-for-betterhelp">Password set for {currentUser?.email}</StepHeading>
                <Typography variant="body1">
                  Need to change? You can reset your password next time you login after completing booking.
                </Typography>
                <StepNextButton className={classes.accountCreatedNextButton} onClick={() => props.onNext({ isLoggedIn: authStore.isLoggedIn })} />
              </StepContent>
              <StepNavigator onBack={props.onBack} />
            </StepContainer>
          );
        }

        return (
          <StepContainer>
            <StepContent className={classes.stepContent}>
              <StepHeading data-testid="confirm-your-info">First, let’s confirm your information</StepHeading>
              <StepForm>
                <Box display="flex" flexDirection="column" gap="1rem">
                  <TextInput
                    trim
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label="Legal First Name"
                  />
                  <OptionalBehindButton
                    buttonText="Add preferred name (optional)"
                  >
                    <TextInput
                      fullWidth
                      id="preferredName"
                      name="preferredName"
                      label="Preferred name (optional)"
                      color="secondary"
                    />
                  </OptionalBehindButton>
                  <TextInput
                    trim
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label="Legal Last Name"
                  />

                  <MaskedInput
                    name="mobilePhone"
                    label={l10n.matching.payment.mobilePhone}
                    type="phone"
                    className={classes.phoneInput}
                  />

                  <div className={classes.section}>
                    <Typography variant="h4" className={classes.sectionTitle}>Create an account</Typography>
                    <div className={classes.sectionContent}>
                      <TextInput
                        fullWidth
                        label={l10n.onboarding.account.email}
                        type="email"
                        name="email"
                        color="secondary"
                        disabled
                        autoComplete="username"
                      />
                      <PasswordField
                        fullWidth
                        label={l10n.onboarding.account.password}
                        name="password"
                        stackValidatorsOnMobile
                      />
                    </div>

                    <Box mt={4} mb={8}>
                      <Typography variant="body1" component="p" className={classes.consentLabel} color="textPrimary">
                        {l10n.matching.payment.accept}
                      </Typography>
                      <FormGroup className={classes.consentFormGroup}>
                        <CheckboxWithLabel
                          showError
                          name="privacyPolicy"
                          color="secondary"
                          label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/privacy" text={l10n.general.privacy} />}
                        />
                        <CheckboxWithLabel
                          showError
                          name="treatmentConsent"
                          color="secondary"
                          label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/terms-of-use" text={l10n.general.terms} />}
                        />
                        <CheckboxWithLabel
                          showError
                          name="noticeOfPrivacyPolicy"
                          color="secondary"
                          label={<ExternalLink fontWeight={400} to="https://www.joinuplift.co/privacy/npp" text={l10n.matching.payment.privacy} />}
                        />
                      </FormGroup>
                    </Box>
                  </div>

                </Box>
              </StepForm>
            </StepContent>
            <StepNavigator onBack={props.onBack}>
              <StepNextButton loading={formik.isSubmitting} />
            </StepNavigator>
          </StepContainer>
        );
      }}
    </Formik>
  );
};

type CreateAccountFormType = {
  firstName: string;
  lastName: string;
  preferredName: string;
  mobilePhone: string;
  email: string;
  password: string;
  privacyPolicy: boolean;
  treatmentConsent: boolean;
  noticeOfPrivacyPolicy: boolean;
};

const validationSchema = (l10n: typeof l10nEnService): Yup.ObjectSchema<CreateAccountFormType> =>
  Yup.object({
    firstName: applyNameValidator(Yup.string())
      .defined(l10n.validations.enterFirstName),
    lastName: applyNameValidator(Yup.string())
      .defined(l10n.validations.enterLastName),
    preferredName: applyNameValidator(Yup.string(), { excludeEmptyString: true }),
    mobilePhone: VALIDATION_MOBILE_PHONE,
    email: Yup.string().email(l10n.validations.invalidEmail).test('ascii-email', l10n.validations.validEmailOnly, isValidEmail).required(l10n.validations.requiredEmail),
    password: Yup.string()
      .max(125, l10n.validations.passwordMax)
      .required(l10n.validations.requiredPass)
      .matches(PASSWORD_MATCHES, l10n.validations.passwordMatches),
    privacyPolicy: Yup.bool().oneOf([ true ], l10n.validations.required),
    treatmentConsent: Yup.bool().oneOf([ true ], l10n.validations.required),
    noticeOfPrivacyPolicy: Yup.bool().oneOf([ true ], l10n.validations.required),
  });

const useCreateAccountStyles = makeStyles({ name: 'CreateAccount' })(theme => ({
  stepHeader: {
    wordBreak: 'break-word'
  },
  phoneInput: {
    display: 'block',
    width: '220px'
  },
  stepContent: {
    paddingBottom: 0,
    maxWidth: `calc(508px + ${theme.spacing(4)})`,
  },
  section: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    margin: theme.spacing(0, -2),
    background: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,

    [theme.breakpoints.up('sm')]: {
      borderTop: 'none',
      background: 'unset',
      margin: 'unset',
      paddingBottom: 'unset'
    }
  },
  sectionContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      padding: 'unset',
      border: 'none'
    }
  },
  sectionTitle: {
    paddingLeft: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      paddingLeft: 'unset'
    }
  },
  disclaimer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(0, 2),
    '& a': {
      color: 'inherit'
    },

    [theme.breakpoints.up('sm')]: {
      padding: 'unset',
      margin: theme.spacing(4, 0, 3)
    },
  },
  content: {
    maxWidth: `calc(508px + ${theme.spacing(4)})`,
  },
  accountCreatedContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'center',
    textAlign: 'center',
  },
  checkCircleIcon: {
    boxSizing: 'border-box',
    width: '60px',
    height: '60px',
    color: theme.palette.success.main,
  },
  accountCreatedNextButton: {
    marginTop: theme.spacing(3),
  },
  consentLabel: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    fontWeight: 450,
  },
  consentFormGroup: {
    paddingLeft: theme.spacing(1.5),
  }
}));
